import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box } from '@mui/material';

// Sessions Chart Component (Area Chart)
export const SessionsChart = ({ data = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Only use real data from API - no dummy data
  const chartData = data.length > 0 ? data : [];
  
  // If no data, show empty state
  if (chartData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 250,
        color: isDark ? '#718096' : '#718096',
        fontSize: '0.875rem'
      }}>
        No data available
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={isDark ? "#6366f1" : "#1976d2"} stopOpacity={0.3} />
            <stop offset="95%" stopColor={isDark ? "#6366f1" : "#1976d2"} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"} />
        <XAxis
          dataKey="date"
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <YAxis
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
            borderRadius: "8px",
            color: isDark ? "#ffffff" : "#1a202c",
          }}
        />
        <Area
          type="monotone"
          dataKey="users"
          stroke={isDark ? "#818cf8" : "#1976d2"}
          fillOpacity={1}
          fill="url(#colorSessions)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Page Views Chart Component (Grouped Bar Chart) - Now for Organizations
export const PageViewsChart = ({ data = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Only use real data from API - no dummy data
  const chartData = data.length > 0 ? data : [];

  // If no data, show empty state
  if (chartData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: 250,
        color: isDark ? '#718096' : '#718096',
        fontSize: '0.875rem'
      }}>
        No data available
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart 
        data={chartData} 
        margin={{ top: 15, right: 20, left: 10, bottom: 10 }}
        barCategoryGap="20%"
      >
        <defs>
          <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={isDark ? "#6366f1" : "#1976d2"} stopOpacity={0.9} />
            <stop offset="95%" stopColor={isDark ? "#6366f1" : "#1976d2"} stopOpacity={0.7} />
          </linearGradient>
          <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={isDark ? "#818cf8" : "#42a5f5"} stopOpacity={0.9} />
            <stop offset="95%" stopColor={isDark ? "#818cf8" : "#42a5f5"} stopOpacity={0.7} />
          </linearGradient>
        </defs>
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke={isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"} 
          vertical={false}
        />
        <XAxis
          dataKey="month"
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem', fontWeight: 500 }}
          axisLine={{ stroke: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }}
          tickLine={{ stroke: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }}
        />
        <YAxis
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
          axisLine={{ stroke: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }}
          tickLine={{ stroke: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
            borderRadius: "8px",
            color: isDark ? "#ffffff" : "#1a202c",
            boxShadow: isDark 
              ? "0 4px 12px rgba(0, 0, 0, 0.5)" 
              : "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
          cursor={{ fill: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)" }}
        />
        <Legend
          wrapperStyle={{
            color: isDark ? "#cbd5e1" : "#4a5568",
            fontSize: "0.75rem",
            paddingTop: "10px",
          }}
          iconType="square"
        />
        <Bar 
          dataKey="views" 
          fill="url(#colorViews)" 
          name="Count"
          radius={[4, 4, 0, 0]}
          maxBarSize={60}
        />
        {chartData.some(d => d.followers > 0) && (
          <Bar 
            dataKey="followers" 
            fill="url(#colorFollowers)" 
            name="Followers"
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};

// User Growth Chart (Line Chart)
export const UserGrowthChart = ({ data = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const chartData = data.length > 0 ? data : Array.from({ length: 12 }, (_, i) => ({
    month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
    users: Math.floor(Math.random() * 5000) + 10000,
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"} />
        <XAxis
          dataKey="month"
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <YAxis
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
            borderRadius: "8px",
            color: isDark ? "#ffffff" : "#1a202c",
          }}
        />
        <Line
          type="monotone"
          dataKey="users"
          stroke={isDark ? "#2563eb" : "#2563eb"}
          strokeWidth={2}
          dot={{ fill: isDark ? "#2563eb" : "#2563eb", r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Sub-Org Distribution Chart (Donut/Pie Chart)
export const SubOrgDistributionChart = ({ data = [] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // This would typically be a PieChart, but for now showing as bar
  const chartData = data.length > 0 ? data : [
    { name: 'Active', value: 12 },
    { name: 'Pending', value: 3 },
    { name: 'Inactive', value: 2 },
  ];

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"} />
        <XAxis
          dataKey="name"
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <YAxis
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
            borderRadius: "8px",
            color: isDark ? "#ffffff" : "#1a202c",
          }}
        />
        <Bar dataKey="value" fill={isDark ? "#6366f1" : "#1976d2"} />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Performance Graph Chart (Multi-line chart for sub-org performance)
export const PerformanceGraphChart = ({ data = [], height = 300 }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Transform data: each sub-org has data_points array, we need to combine them by date
  const dateMap = {};

  data.forEach((subOrg) => {
    if (subOrg.data_points && Array.isArray(subOrg.data_points)) {
      const orgId = subOrg.sub_org_id || subOrg.id || `org_${data.indexOf(subOrg)}`;
      subOrg.data_points.forEach((point) => {
        const date = point.date || '';
        if (!dateMap[date]) {
          dateMap[date] = { date };
        }
        // Store performance score with sub-org id as key (safer than name)
        dateMap[date][`score_${orgId}`] = point.performance_score || 0;
        dateMap[date][`likes_${orgId}`] = point.likes || 0;
        dateMap[date][`saves_${orgId}`] = point.saves || 0;
        dateMap[date][`views_${orgId}`] = point.views || 0;
        dateMap[date][`posts_${orgId}`] = point.posts || 0;
      });
    }
  });

  // Convert to array and sort by date
  const sortedData = Object.values(dateMap).sort((a, b) => {
    return new Date(a.date) - new Date(b.date);
  });

  // Format dates for display
  const formattedData = sortedData.map(item => ({
    ...item,
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  if (formattedData.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height,
        color: isDark ? '#718096' : '#718096',
        fontSize: '0.875rem'
      }}>
        No performance data available
      </Box>
    );
  }

  // Get unique colors for each sub-org
  const colors = [
    isDark ? "#818cf8" : "#1976d2",
    isDark ? "#f472b6" : "#ec4899",
    isDark ? "#34d399" : "#10b981",
    isDark ? "#fbbf24" : "#f59e0b",
    isDark ? "#a78bfa" : "#8b5cf6",
  ];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={formattedData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"} />
        <XAxis
          dataKey="date"
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis
          stroke={isDark ? "#cbd5e1" : "#718096"}
          tick={{ fill: isDark ? "#cbd5e1" : "#718096", fontSize: '0.75rem' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
            borderRadius: "8px",
            color: isDark ? "#ffffff" : "#1a202c",
          }}
        />
        <Legend
          wrapperStyle={{
            color: isDark ? "#cbd5e1" : "#4a5568",
            fontSize: "0.75rem",
            paddingTop: "10px",
          }}
        />
        {data.slice(0, 5).map((subOrg, index) => {
          const color = colors[index % colors.length];
          const orgId = subOrg.sub_org_id || subOrg.id || `org_${index}`;
          const scoreKey = `score_${orgId}`;
          return (
            <Line
              key={orgId}
              type="monotone"
              dataKey={scoreKey}
              name={subOrg.sub_org_name || `Club ${index + 1}`}
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, r: 3 }}
              activeDot={{ r: 5 }}
            />
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
};
